import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import demoService from '../../../../services/demoService';
import courseService from '../../../../services/courseService';
import type { DemoSlot, IDemoBooking } from '../../../../types/demo';
import type { ICourse } from '../../../../types/course';
import Loader from '../../../../components/common/Loader';
import { showToast } from '../../../../utils/showToast';
import { Trash2, Search, CalendarCheck, Plus, Power } from 'lucide-react';

const bookingCourseTitle = (booking: IDemoBooking) => typeof booking.course === 'string' ? booking.legacyCourseLabel || booking.course : booking.course.title;

const ViewDemoBookings: React.FC = () => {
  const { user, isLoading: isAuthLoading } = useAuth();
  const [allBookings, setAllBookings] = useState<IDemoBooking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<IDemoBooking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [slots, setSlots] = useState<DemoSlot[]>([]);
  const [courses, setCourses] = useState<ICourse[]>([]);
  const [slotForm, setSlotForm] = useState({ courseId: '', date: '', startTime: '', endTime: '', capacity: '1' });
  const [isSavingSlot, setIsSavingSlot] = useState(false);

  // --- Your original data fetching and handler logic is preserved below ---

  const fetchBookings = async () => {
    setIsLoading(true);
    try {
      const data = await demoService.getAllDemoBookings();
      setAllBookings(data);
    } catch (err: any) {
      showToast(err.message, { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSlots = async () => {
    try { setSlots(await demoService.getDemoSlots()); } catch (err: any) { showToast(err.message, { type: 'error' }); }
  };

  useEffect(() => {
    if (!isAuthLoading && user?.isAdmin) {
      fetchBookings();
      fetchSlots();
      courseService.getAllPublishedCourses().then(setCourses).catch((err: Error) => showToast(err.message, { type: 'error' }));
    }
  }, [isAuthLoading, user]);

  const handleCreateSlot = async (event: React.FormEvent) => {
    event.preventDefault(); setIsSavingSlot(true);
    try {
      const slot = await demoService.createDemoSlot({ ...slotForm, capacity: Number(slotForm.capacity) });
      setSlots((current) => [...current, slot].sort((a, b) => `${a.date}${a.startTime}`.localeCompare(`${b.date}${b.startTime}`)));
      setSlotForm({ courseId: '', date: '', startTime: '', endTime: '', capacity: '1' }); showToast('Demo availability added.', { type: 'success' });
    } catch (err: any) { showToast(err.message, { type: 'error' }); } finally { setIsSavingSlot(false); }
  };

  const handleToggleSlot = async (slot: DemoSlot) => {
    try { const updated = await demoService.updateDemoSlot(slot.id, { isActive: !slot.isActive }); setSlots((current) => current.map((item) => item.id === updated.id ? updated : item)); }
    catch (err: any) { showToast(err.message, { type: 'error' }); }
  };

  const handleDeleteSlot = async (slot: DemoSlot) => {
    if (!window.confirm(`Remove the ${slot.date} ${slot.startTime} demo slot?`)) return;
    try { await demoService.deleteDemoSlot(slot.id); setSlots((current) => current.filter((item) => item.id !== slot.id)); showToast('Demo slot removed.', { type: 'success' }); }
    catch (err: any) { showToast(err.message, { type: 'error' }); }
  };

  useEffect(() => {
    let filtered = allBookings;

    if (statusFilter !== 'All') {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }

    if (searchTerm) {
      const lowercasedFilter = searchTerm.toLowerCase();
      filtered = filtered.filter(booking =>
        booking.fullName.toLowerCase().includes(lowercasedFilter) ||
        booking.email.toLowerCase().includes(lowercasedFilter)
      );
    }

    setFilteredBookings(filtered);
  }, [searchTerm, statusFilter, allBookings]);

  const handleUpdateStatus = async (bookingId: string, status: 'Pending' | 'Contacted' | 'Closed') => {
    setIsProcessing(bookingId);
    try {
      const updatedBooking = await demoService.updateDemoBookingStatus(bookingId, status);
      showToast('Status updated successfully!', { type: 'success' });
      setAllBookings(prev => prev.map(b => b._id === bookingId ? updatedBooking : b));
    } catch (err: any) {
      showToast(err.message, { type: 'error' });
    } finally {
      setIsProcessing(null);
    }
  };

  const handleDeleteBooking = async (booking: IDemoBooking) => {
    if (!window.confirm(`Are you sure you want to delete the booking for ${booking.fullName}?`)) return;
    setIsProcessing(booking._id);
    try {
      const message = await demoService.deleteDemoBooking(booking._id);
      showToast(message, { type: 'success' });
      setAllBookings(prev => prev.filter(b => b._id !== booking._id));
    } catch (err: any) {
      showToast(err.message, { type: 'error' });
    } finally {
      setIsProcessing(null);
    }
  };

  if (isAuthLoading || isLoading) {
    return <Loader />;
  }

  const getStatusColor = (status: 'Pending' | 'Contacted' | 'Closed') => {
    switch (status) {
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Contacted': return 'bg-blue-100 text-blue-800';
      case 'Closed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const renderActionControls = (booking: IDemoBooking) => (
    <div className="flex justify-end items-center gap-2">
      <select
        value={booking.status}
        onChange={(e) => handleUpdateStatus(booking._id, e.target.value as any)}
        disabled={!!isProcessing}
        className="w-32 px-2 py-1.5 border border-gray-300 rounded-md text-xs font-medium bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
        onClick={(e) => e.stopPropagation()} // Prevent card link navigation
      >
        <option value="Pending">Pending</option>
        <option value="Contacted">Contacted</option>
        <option value="Closed">Closed</option>
      </select>
      <button 
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteBooking(booking);
        }} 
        disabled={!!isProcessing} 
        className="p-2 text-red-600 hover:bg-red-100 rounded-full disabled:opacity-50 transition-colors" 
        title="Delete Booking"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      <section className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div><h2 className="text-xl font-bold text-gray-800">Manage demo availability</h2><p className="mt-1 text-sm text-gray-500">Create schedules for a specific published course.</p></div>
          <form onSubmit={handleCreateSlot} className="grid w-full gap-2 sm:grid-cols-3 lg:max-w-3xl">
            <select required value={slotForm.courseId} onChange={(e) => setSlotForm({ ...slotForm, courseId: e.target.value })} className="rounded-lg border border-gray-300 px-3 py-2 text-sm" aria-label="Course"><option value="">Select course</option>{courses.map((course) => <option key={course._id} value={course._id}>{course.title}</option>)}</select>
            <input required type="date" value={slotForm.date} onChange={(e) => setSlotForm({ ...slotForm, date: e.target.value })} className="rounded-lg border border-gray-300 px-3 py-2 text-sm" aria-label="Demo date" />
            <input required type="time" value={slotForm.startTime} onChange={(e) => setSlotForm({ ...slotForm, startTime: e.target.value })} className="rounded-lg border border-gray-300 px-3 py-2 text-sm" aria-label="Demo start time" />
            <input required type="time" value={slotForm.endTime} onChange={(e) => setSlotForm({ ...slotForm, endTime: e.target.value })} className="rounded-lg border border-gray-300 px-3 py-2 text-sm" aria-label="Demo end time" />
            <input required min="1" type="number" value={slotForm.capacity} onChange={(e) => setSlotForm({ ...slotForm, capacity: e.target.value })} className="rounded-lg border border-gray-300 px-3 py-2 text-sm" aria-label="Slot capacity" />
            <button disabled={isSavingSlot} className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"><Plus size={16} />Add slot</button>
          </form>
        </div>
        {slots.length > 0 && <div className="mt-5 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">{slots.map((slot) => <div key={slot.id} className={`flex items-center justify-between rounded-lg border p-3 text-sm ${slot.isActive ? 'border-gray-200 bg-gray-50' : 'border-gray-200 bg-gray-100 opacity-70'}`}><div><p className="font-semibold text-gray-800">{slot.course?.title || 'Legacy slot'}</p><p className="text-sm text-gray-700">{slot.date} · {slot.startTime}–{slot.endTime}</p><p className="text-xs text-gray-500">{slot.availableSeats}/{slot.capacity} seats · {slot.timezone}</p></div><div className="flex gap-1"><button onClick={() => handleToggleSlot(slot)} className="rounded p-2 text-gray-600 hover:bg-white" title={slot.isActive ? 'Deactivate slot' : 'Activate slot'}><Power size={16} /></button><button onClick={() => handleDeleteSlot(slot)} className="rounded p-2 text-red-600 hover:bg-red-50" title="Delete slot"><Trash2 size={16} /></button></div></div>)}</div>}
      </section>
      <header className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Demo Booking Requests</h1>
            <p className="text-gray-500 mt-1">Review and manage all incoming demo requests.</p>
          </div>
          <div className="flex flex-col sm:flex-row w-full md:w-auto gap-2">
            <div className="relative w-full sm:w-60">
              <select 
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-full bg-gray-50 focus:ring-2 focus:ring-blue-500 transition appearance-none"
              >
                  <option value="All">All Statuses</option>
                  <option value="Pending">Pending</option>
                  <option value="Contacted">Contacted</option>
                  <option value="Closed">Closed</option>
              </select>
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 border border-gray-300 rounded-full bg-gray-50 focus:ring-2 focus:ring-blue-500 transition"
              />
            </div>
          </div>
        </div>
      </header>
      
      {filteredBookings.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl shadow-sm border">
          <CalendarCheck className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">No Bookings Found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || statusFilter !== 'All' ? 'No bookings match your criteria.' : 'There are no demo bookings to display.'}
          </p>
        </div>
      ) : (
        <>
          <div className="hidden md:block bg-white rounded-2xl shadow-sm border overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Contact Details</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Course</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Requested On</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredBookings.map(booking => (
                  <tr key={booking._id} className={isProcessing === booking._id ? 'opacity-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{booking.fullName}</div>
                      <div className="text-sm text-gray-500">{booking.email}</div>
                      <div className="text-sm text-gray-500">{booking.phone}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">{bookingCourseTitle(booking)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">{new Date(booking.createdAt).toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${getStatusColor(booking.status)}`}>
                        {booking.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {renderActionControls(booking)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-4">
            {filteredBookings.map(booking => (
              <div key={booking._id} className="bg-white p-4 rounded-2xl shadow-sm border space-y-4">
                <div>
                  <div className="flex justify-between items-start">
                    <p className="font-bold text-gray-900">{booking.fullName}</p>
                    <span className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${getStatusColor(booking.status)}`}>
                      {booking.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">{booking.email} | {booking.phone}</p>
                </div>
                <div className="border-t pt-4 space-y-2 text-sm">
                  <p><span className="font-semibold text-gray-700">Course:</span> {bookingCourseTitle(booking)}</p>
                  <p><span className="font-semibold text-gray-700">Requested:</span> {new Date(booking.createdAt).toLocaleString()}</p>
                </div>
                <div className="border-t pt-4">
                  {renderActionControls(booking)}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ViewDemoBookings;
